import { styled } from '@mui/material'
import { PageArea, PageProps } from '..'
import { useProjectPrivateContext } from '../../context/projectContext'
import { projectPrivate } from '../../higher-order-components/projectPrivate'
import { VersionReportHeader } from './components/header'
import { VersionReportPage } from './components/pages/facade'
import { useCurrentVersion } from './hooks/currentReport'
import { useEditingState } from './hooks/editingState'
import { useEvaluate } from './hooks/evaluate'
import { useDiscussionPanel } from './hooks/useDiscussionPanel'
import { useVersionSelectMenu } from './hooks/useVersionSelectMenu'
import { DiscussionPanel } from './components/discussion-panel/index'
import { VersionSelectMenu } from './components/version-select-menu'
import { Button } from '../../components/buttons'
import Loading, {
  useLoading,
} from '../../components/process-state-notifications/Loading'
import { useCallback, useEffect } from 'react'
import { useTemplateData } from './hooks/useTemplateData'
import { useReportData } from './hooks/useReportData'
import { intl } from '../../../i18n'
import { DiscussionProvider } from './context/DiscussionContext'

type ProjectVersionReportProps = PageProps
export const ProjectVersionReport = projectPrivate(
  ({ uuid }: ProjectVersionReportProps) => {
    const { projectUuid } = useProjectPrivateContext()
    const { isLoading, showLoading, hideLoading } = useLoading()
    const {
      currentPage,
      currentVersion,
      updateVersionUuid,
      updateCurrentVersion,
      updatePageContent,
      storedVersionUuid,
      initialized,
      ...others
    } = useCurrentVersion(uuid, projectUuid)

    const { evaluate } = useEvaluate(
      currentVersion,
      currentPage,
      updatePageContent
    )
    const discussionPanel = useDiscussionPanel()
    const {
      isOpen: isVersionSelectMenuOpen,
      toggle: toggleVersionSelectMenu,
      open: openVersionSelectMenu,
      close: closeVersionSelectMenu,
      selectReport,
      handleAfterCreateReport,
    } = useVersionSelectMenu(updateVersionUuid)

    const templateData = useTemplateData()
    const reportData = useReportData(templateData.selectedTemplate?.uuid)

    const { editing, ...editingHandlers } = useEditingState(
      currentVersion,
      currentPage,
      updateCurrentVersion,
      reportData.refreshReports
    )

    const handleAfterDeleteVersion = useCallback(async () => {
      updateVersionUuid(undefined)
      await reportData.refreshReports()
      openVersionSelectMenu()
    }, [openVersionSelectMenu, updateVersionUuid, reportData.refreshReports])

    useEffect(() => {
      if (initialized && !storedVersionUuid) {
        openVersionSelectMenu()
      }
    }, [initialized, storedVersionUuid, openVersionSelectMenu])

    if (!initialized) {
      return (
        <PageArea>
          <Loading isLoading={true} />
        </PageArea>
      )
    }

    return (
      <PageArea>
        <DiscussionProvider
          discussionPanel={discussionPanel}
          versionPageUuid={currentPage?.uuid}
          currentVersionUuid={currentVersion?.uuid}
          templatePageUuid={currentPage?.templatePageUuid}
          versionList={reportData.reports}
        >
          <Root>
            <VersionReportHeader
              currentPage={currentPage}
              currentVersion={currentVersion}
              {...others}
              editing={editing}
              {...editingHandlers}
              evaluate={evaluate}
              discussionPanel={discussionPanel}
              isVersionSelectMenuOpen={isVersionSelectMenuOpen}
              onReportMenuToggle={toggleVersionSelectMenu}
              onAfterDeleteVersion={handleAfterDeleteVersion}
              showLoading={showLoading}
              hideLoading={hideLoading}
            />
            <MainContent>
              <VersionSelectMenu
                projectUuid={projectUuid}
                open={isVersionSelectMenuOpen}
                onClose={closeVersionSelectMenu}
                onReportSelect={selectReport}
                onAfterCreateReport={handleAfterCreateReport}
                templateData={templateData}
                reportData={reportData}
              />
              <PageContentArea isDiscussionOpen={discussionPanel.isOpen}>
                {currentVersion ? (
                  <>
                    <VersionReportPage
                      snapshotUuid={currentVersion?.snapshotUuid}
                      page={currentPage}
                      editing={editing}
                      updatePageContent={updatePageContent}
                      showLoading={showLoading}
                      hideLoading={hideLoading}
                      {...others}
                    />
                  </>
                ) : (
                  <TemplateSelectionContainer>
                    <TemplateSelectionContent>
                      <TemplateSelectionButtonContainer>
                        <Button onClick={openVersionSelectMenu}>
                          {intl.formatMessage({
                            id: 'versionReport.selectReport',
                          })}
                        </Button>
                      </TemplateSelectionButtonContainer>
                    </TemplateSelectionContent>
                  </TemplateSelectionContainer>
                )}
              </PageContentArea>
              {currentPage && currentVersion && (
                <DiscussionPanel projectUuid={projectUuid} />
              )}
            </MainContent>
            <Loading isLoading={isLoading} />
          </Root>
        </DiscussionProvider>
      </PageArea>
    )
  }
)
const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '16px',
  height: '100%',
})

const MainContent = styled('div')({
  display: 'flex',
  flex: 1,
  position: 'relative',
  overflow: 'hidden',
})

const PageContentArea = styled('div')<{ isDiscussionOpen: boolean }>(
  ({ isDiscussionOpen }) => ({
    flex: 1,
    overflow: 'auto',
    ...(isDiscussionOpen && {
      paddingRight: '16px',
    }),
    transition: 'all 0.3s ease',
  })
)

const TemplateSelectionContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  width: '100%',
})

const TemplateSelectionContent = styled('div')({
  textAlign: 'center',
  padding: '40px',
  maxWidth: '400px',
})

const TemplateSelectionButtonContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
})
